import { useEffect, useState, type PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router";

import { supabase } from "../lib/supabase";
import { workerDatabase } from "../offline/database";

type GuardState = "checking" | "authenticated" | "anonymous";

export function AuthGuard({ children }: PropsWithChildren) {
  const [state, setState] = useState<GuardState>("checking");
  const location = useLocation();

  useEffect(() => {
    let active = true;
    function purgeOfflineAccess(signOut: boolean) {
      window.setTimeout(() => {
        void (async () => {
          await workerDatabase.purge();
          if (signOut) await supabase.auth.signOut({ scope: "local" });
        })();
      }, 0);
    }
    async function classify(
      session: { user: { id: string } } | null,
    ): Promise<void> {
      if (!session) {
        if (active) {
          setState("anonymous");
          purgeOfflineAccess(false);
        }
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", session.user.id)
        .maybeSingle();
      if (!active) return;
      if (profile) {
        setState("authenticated");
        return;
      }
      setState("anonymous");
      purgeOfflineAccess(true);
    }
    void supabase.auth.getSession().then(({ data }) => classify(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      void classify(session);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  if (state === "checking") {
    return (
      <main className="page" aria-busy="true">
        <p>Opening your support hub…</p>
      </main>
    );
  }
  if (state === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}
